"use strict";
/**
 * Chat body parsing for the read-cursor / tail-read optimization.
 *
 * A chat body is an optional `# Title` H1 followed by a sequence of `## {header}`
 * blocks (the configured user/ai header strings, e.g. `## Rafa:` / `## AI:`). The
 * header label is NOT hardcoded — callers pass the configured value from
 * `.loom/settings.json` (see app `chatNames`). This module owns the block model so
 * the cursor (last AI block index) is computed the same way on write (append) and
 * read (tail), and never drifts.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseChatBlocks = parseChatBlocks;
exports.lastAiBlockIndex = lastAiBlockIndex;
exports.tailAfterBlock = tailAfterBlock;
exports.appendChatBlock = appendChatBlock;
const BLOCK_HEADER_RE = /^##\s+(.+?)\s*$/;
/** Split a chat body into its `## {header}` blocks, in order. Content before the
 *  first `## ` header (e.g. the `# Title` H1) is not a block and is dropped. */
function parseChatBlocks(body) {
    const lines = body.split('\n');
    const blocks = [];
    let current = null;
    for (const line of lines) {
        const m = line.match(BLOCK_HEADER_RE);
        if (m) {
            if (current)
                blocks.push({ header: current.header, text: current.lines.join('\n') });
            current = { header: m[1].trim(), lines: [line] };
        }
        else if (current) {
            current.lines.push(line);
        }
    }
    if (current)
        blocks.push({ header: current.header, text: current.lines.join('\n') });
    return blocks;
}
/** 0-based index of the last block authored by the AI (header === aiHeader), or -1. */
function lastAiBlockIndex(body, aiHeader) {
    const blocks = parseChatBlocks(body);
    for (let i = blocks.length - 1; i >= 0; i--) {
        if (blocks[i].header === aiHeader)
            return i;
    }
    return -1;
}
/**
 * The text of all blocks AFTER `blockIndex` (the new turns since the AI last replied),
 * joined as they appear in the doc. `blockIndex < 0` means "no AI block yet" → return
 * every block (the whole conversation so far). Returns '' when nothing follows.
 */
function tailAfterBlock(body, blockIndex) {
    const blocks = parseChatBlocks(body);
    const from = blockIndex < 0 ? 0 : blockIndex + 1;
    return blocks.slice(from).map(b => b.text.trim()).filter(Boolean).join('\n\n');
}
/**
 * Append a new `## {header}` block to a chat body, normalizing the seam so the
 * result always has exactly one blank line before the header and one between the
 * header and the message — regardless of any trailing whitespace the existing body
 * or the incoming message already carry.
 *
 * This is the single source of truth for chat-block appending (used by both the
 * MCP `loom_append_to_chat` tool and the app `chatReply` sampling path), so the two
 * never drift in format and trailing newlines can't compound into widening gaps.
 *
 * Trailing whitespace on `body` is always safe to drop; only *leading newlines* are
 * stripped (not leading spaces) so an opening indented code line survives. `header`
 * is the full label written after `## ` (e.g. `AI:` or `Rafa:`), caller-configured.
 */
function appendChatBlock(existingBody, header, body) {
    const base = existingBody.replace(/\s+$/, '');
    const text = body.replace(/^\n+/, '').replace(/\s+$/, '');
    const block = `## ${header}\n\n${text}`;
    return base ? `${base}\n\n${block}` : block;
}
